import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Button, Loading, SmallLoader } from 'framework/ui/mui-builder';
import { TREE_PAGE_SIZE } from '../../constants';
import { TreeChildrenParams, useCoreServiceClient } from '../../api/coreServiceClient';
import useAsyncData from '../../hooks/useAsyncData';
import { useDebouncedValue } from 'framework/ViewTypes/hooks';
import EmptyState from '../../components/EmptyState';
import ErrorNotice from '../../components/ErrorNotice';
import FilterChip from '../../components/FilterChip';
import OriginDot from '../../components/OriginDot';
import StatTile from '../../components/StatTile';
import type { ChildrenPageDto, FacetValueDto, ProcessRunDto, TicketRowDto } from '../../types';
import { formatNumber, getErrorMessage } from '../../utils/format';
import { RunRow, TicketRow } from './TicketRows';

type Mode = 'all' | 'process' | 'person';
type Sort = 'sla' | 'updated' | 'created' | 'title';
type FacetKey = 'service' | 'state' | 'tracker' | 'project' | 'process' | 'initiator' | 'flag';

const SORT_OPTIONS: { value: Sort; label: string }[] = [
  { value: 'sla', label: 'Sort: SLA first' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'created', label: 'Newest' },
  { value: 'title', label: 'Title' },
];

const FACET_LIMIT = 12;
const KEPT_FILTERS: FacetKey[] = ['service', 'state', 'tracker', 'project', 'flag'];
const DEBOUNCE_MS = 300;

type Filters = Partial<Record<FacetKey, string>>;

type FacetRowProps = {
  facetKey: FacetKey;
  label: string;
  values?: FacetValueDto[];
  filters: Filters;
  onChange: (key: FacetKey, value?: string) => void;
};

const FacetRow = ({ facetKey, label, values, filters, onChange }: FacetRowProps) => {
  if (!values || (values.length < 2 && !filters[facetKey])) {
    return null;
  }

  return (
    <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
      <Typography variant="caption" color="text.secondary" sx={{ width: 64, flex: 'none' }}>
        {label}
      </Typography>
      {values.slice(0, FACET_LIMIT).map(value => (
        <FilterChip
          key={value.key}
          label={value.label}
          count={formatNumber(value.count)}
          isActive={filters[facetKey] === value.key}
          onClick={() => onChange(facetKey, filters[facetKey] === value.key ? undefined : value.key)}
        />
      ))}
    </Stack>
  );
};

type NodePanelProps = {
  nodeId: string;
  onOpenNode: (nodeId: string) => void;
};

/** The selected node: counters, mode switch, facets, and the paginated table of tickets or process runs. */
const NodePanel = ({ nodeId, onOpenNode }: NodePanelProps) => {
  const client = useCoreServiceClient();
  const [mode, setMode] = useState<Mode>('all');
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<Sort>('sla');
  const [isOpenOnly, setOpenOnly] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, DEBOUNCE_MS);

  const [extraPages, setExtraPages] = useState<ChildrenPageDto[]>([]);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<Nullable<string>>(null);

  const buildParams = useCallback(
    (pageNumber: number): TreeChildrenParams => ({
      openOnly: isOpenOnly,
      sort: sort.toUpperCase(),
      pageNumber,
      pageSize: TREE_PAGE_SIZE,
      view: mode === 'process' ? 'processes' : undefined,
      origin: mode === 'person' ? 'person' : undefined,
      q: debouncedSearch.trim() || undefined,
      ...filters,
    }),
    [filters, isOpenOnly, mode, sort, debouncedSearch],
  );

  const firstPage = useAsyncData(() => client.getTreeChildren(nodeId, buildParams(0)), [client, nodeId, buildParams]);
  const page = firstPage.data;

  useEffect(() => {
    setExtraPages([]);
    setMoreError(null);
  }, [page]);

  const tickets: TicketRowDto[] = useMemo(
    () => [page, ...extraPages].flatMap(item => item?.tickets?.result ?? []),
    [page, extraPages],
  );
  const runs: ProcessRunDto[] = useMemo(
    () => [page, ...extraPages].flatMap(item => item?.runs?.result ?? []),
    [page, extraPages],
  );

  const isRunsView = page?.view === 'processes';
  const loaded = isRunsView ? runs.length : tickets.length;
  const total = (isRunsView ? page?.runs?.totalElements : page?.tickets?.totalElements) ?? 0;

  const loadMore = async () => {
    setLoadingMore(true);
    setMoreError(null);

    try {
      const next = await client.getTreeChildren(nodeId, buildParams(extraPages.length + 1));

      setExtraPages(previous => [...previous, next]);
    } catch (error) {
      setMoreError(getErrorMessage(error));
    } finally {
      setLoadingMore(false);
    }
  };

  const changeFacet = (key: FacetKey, value?: string) => setFilters(previous => ({ ...previous, [key]: value }));

  const changeMode = (next: Mode, extra: Filters = {}) => {
    setFilters(previous => ({
      ...Object.fromEntries(Object.entries(previous).filter(([key]) => KEPT_FILTERS.includes(key as FacetKey))),
      ...extra,
    }));
    setMode(next);
  };

  const openProcess = (processKey: string) => {
    setSearch('');
    changeMode('process', { process: processKey });
  };

  if (firstPage.error && !page) {
    return <ErrorNotice error={firstPage.error} onRetry={firstPage.reload} />;
  }

  if (!page) {
    return <Loading isInitialPosition />;
  }

  const stats = page.node.stats;

  return (
    <Paper variant="outlined" sx={{ padding: '14px 16px', minWidth: 0, overflowX: 'auto' }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {page.path.map((item, index) => (
          <span key={item.id}>
            {index ? ' / ' : ''}
            {item.id === nodeId ? (
              item.title
            ) : (
              <Link
                component="button"
                type="button"
                color="inherit"
                onClick={() => onOpenNode(item.id)}
                sx={{ font: 'inherit' }}
              >
                {item.title}
              </Link>
            )}
          </span>
        ))}
      </Typography>
      <Typography variant="h5" sx={{ marginBottom: 1 }}>
        {page.node.title}
      </Typography>
      {page.node.owners.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.5 }}>
          Members: {page.node.owners.join(', ')}
        </Typography>
      ) : null}

      <Stack direction="row" gap={1.25} flexWrap="wrap" sx={{ marginBottom: 1.5 }}>
        <StatTile
          value={formatNumber(stats.open)}
          label="open"
          isActive={!filters.flag}
          onClick={() => changeFacet('flag', undefined)}
        />
        <StatTile
          value={formatNumber(stats.overdue)}
          label="overdue"
          tone="error"
          isActive={filters.flag === 'overdue'}
          onClick={() => changeFacet('flag', 'overdue')}
        />
        <StatTile
          value={formatNumber(stats.queued)}
          label="in queues"
          isActive={filters.flag === 'queued'}
          onClick={() => changeFacet('flag', 'queued')}
        />
        <StatTile
          value={formatNumber(stats.unowned)}
          label="no owner"
          tone="warning"
          isActive={filters.flag === 'unowned'}
          onClick={() => changeFacet('flag', 'unowned')}
        />
      </Stack>

      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={mode}
        onChange={(_, next: Nullable<Mode>) => next && changeMode(next)}
        sx={{ marginBottom: 1.5, flexWrap: 'wrap' }}
      >
        <ToggleButton value="all" sx={{ textTransform: 'none' }}>
          All tasks&nbsp;<b>{formatNumber(stats.open)}</b>
        </ToggleButton>
        <ToggleButton value="process" sx={{ textTransform: 'none' }}>
          <OriginDot kind="process" />
          By process&nbsp;<b>{formatNumber(stats.processRuns)}</b>&nbsp;
          <Typography component="span" variant="caption">
            runs · {formatNumber(stats.fromProcesses)} tasks
          </Typography>
        </ToggleButton>
        <ToggleButton value="person" sx={{ textTransform: 'none' }}>
          <OriginDot kind="person" />
          From people&nbsp;<b>{formatNumber(stats.fromPeople)}</b>
        </ToggleButton>
      </ToggleButtonGroup>

      <Stack gap={0.75} sx={{ marginBottom: 1.5 }}>
        {mode === 'process' ? (
          <FacetRow
            facetKey="process"
            label="Process"
            values={page.facets.process}
            filters={filters}
            onChange={changeFacet}
          />
        ) : null}
        {mode === 'person' ? (
          <FacetRow
            facetKey="initiator"
            label="Asked by"
            values={page.facets.initiator}
            filters={filters}
            onChange={changeFacet}
          />
        ) : null}
        <FacetRow
          facetKey="service"
          label="Service"
          values={page.facets.service}
          filters={filters}
          onChange={changeFacet}
        />
        <FacetRow facetKey="state" label="State" values={page.facets.state} filters={filters} onChange={changeFacet} />
        <FacetRow
          facetKey="tracker"
          label="Tracker"
          values={page.facets.tracker}
          filters={filters}
          onChange={changeFacet}
        />
        <FacetRow
          facetKey="project"
          label="Project"
          values={page.facets.project}
          filters={filters}
          onChange={changeFacet}
        />
      </Stack>

      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ marginBottom: 1 }}>
        <TextField
          size="small"
          type="search"
          sx={{ flex: 1, minWidth: 220 }}
          placeholder={
            mode === 'process' ? 'Search process, run, task or owner' : 'Search title, #id, owner or who asked'
          }
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <TextField
          select
          size="small"
          value={sort}
          onChange={event => setSort(event.target.value as Sort)}
          sx={{ minWidth: 180 }}
        >
          {SORT_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          label="open only"
          control={<Checkbox checked={isOpenOnly} onChange={event => setOpenOnly(event.target.checked)} />}
        />
        <Typography variant="body2" color="text.secondary">
          {formatNumber(total)} {isRunsView ? 'runs' : page.flat ? 'tickets (subtasks included)' : 'tickets'}
        </Typography>
        {firstPage.isLoading ? <SmallLoader /> : null}
      </Stack>

      {firstPage.error ? <ErrorNotice error={firstPage.error} onRetry={firstPage.reload} /> : null}

      {!loaded ? (
        <EmptyState>{isRunsView ? 'No process runs here' : 'No tickets match'}</EmptyState>
      ) : (
        <TableContainer sx={{ opacity: firstPage.isLoading ? 0.6 : 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                {isRunsView ? (
                  <>
                    <TableCell>Process run</TableCell>
                    <TableCell>Tasks</TableCell>
                    <TableCell>Waiting for</TableCell>
                    <TableCell>Next SLA</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Ticket</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>SLA</TableCell>
                    <TableCell>{mode === 'person' ? 'Asked by' : 'From'}</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isRunsView
                ? runs.map(run => (
                    <RunRow
                      key={run.runKey}
                      run={run}
                      nodeId={nodeId}
                      openOnly={isOpenOnly}
                      onOpenProcess={openProcess}
                    />
                  ))
                : tickets.map(ticket => (
                    <TicketRow
                      key={ticket.id}
                      ticket={ticket}
                      depth={0}
                      openOnly={isOpenOnly}
                      onOpenProcess={openProcess}
                    />
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loaded < total ? (
        <Box sx={{ marginTop: 1.5 }}>
          <Button variant="outlined" size="small" disabled={isLoadingMore} onClick={loadMore}>
            Show more ({formatNumber(total - loaded)} left)
          </Button>
          {moreError ? (
            <Typography variant="body2" color="error" component="span" sx={{ marginLeft: 1 }}>
              {moreError}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Paper>
  );
};

export default NodePanel;
